const MOBILE_MAX_WIDTH = 767;
const PENDING_LABEL = 'Adding…';
const SUCCESS_LABEL = 'Added ✓';
const SUCCESS_LABEL_DURATION = 1300;
const FLY_DURATION = 280;
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.77, 0, 0.175, 1)';
const CART_ADD_FRIENDLY_ERROR = 'We couldn’t add this item. Please refresh the page and try again.';
const CART_ADD_UNCERTAIN_ERROR = 'We couldn’t confirm whether this item was added. Please check your cart before trying again.';
const VARIANT_REQUIRED_ERROR = 'Please select your lash options and try again.';

const feedbackTimers = new WeakMap();
const feedbackAnimations = new WeakMap();

function isMobileProductContext(context) {
  return context.pageType === 'product' && context.viewportWidth <= MOBILE_MAX_WIDTH;
}

class CartAddUncertainError extends Error {
  constructor(message = CART_ADD_UNCERTAIN_ERROR) {
    super(message);
    this.name = 'CartAddUncertainError';
    this.code = 'CART_ADD_UNCERTAIN';
  }
}

export function applyVariantIdFallback(formData, variantSearch = '') {
  const currentVariantId = String(formData.get('id') || '').trim();
  if (currentVariantId) return currentVariantId;

  const urlVariantId = new URLSearchParams(variantSearch).get('variant') || '';
  if (!/^\d+$/.test(urlVariantId)) return null;

  formData.set('id', urlVariantId);
  return urlVariantId;
}

export async function parseCartAddResponse(response) {
  const contentType = response.headers?.get?.('content-type') || '';
  const body = await response.text();
  const isJsonResponse = /\b(?:application\/json|text\/javascript)\b/i.test(contentType);

  if (!isJsonResponse || response.status >= 500) throw new CartAddUncertainError();

  let data;
  try {
    data = JSON.parse(body);
  } catch {
    throw new CartAddUncertainError();
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new CartAddUncertainError();
  if (!response.ok && !data.status) data.status = response.status;
  const addedItems = Array.isArray(data.items) ? data.items : [data];
  if (!data.status && (!addedItems.length || addedItems.some((item) => !(Number(item?.id) > 0)))) {
    throw new CartAddUncertainError();
  }
  return data;
}

function refreshCartInBackground(refreshCart, onBackgroundError = console.error) {
  Promise.resolve()
    .then(refreshCart)
    .catch(onBackgroundError);
}

async function refreshCartReadOnly() {
  const sectionIds = [...new Set(Array.from(
    document.querySelectorAll('[ref="hdt-cart"][section-id]'),
    (cart) => cart.getAttribute('section-id')
  ).filter(Boolean))];
  if (!sectionIds.length) return;

  // The theme's cart:reload event POSTs to cart/update. Fetch rendered cart
  // sections directly so reconciliation cannot issue another cart mutation.
  const response = await fetch(`${Shopify.routes.root}?sections=${encodeURIComponent(sectionIds.join(','))}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) throw new Error('Unable to refresh the cart.');
  const sections = await response.json();
  if (sectionIds.some((id) => typeof sections?.[id] !== 'string' || !sections[id].trim())) {
    throw new Error('Unable to refresh the cart.');
  }
  const cartHtml = new DOMParser().parseFromString(sections[sectionIds[0]], 'text/html');
  const countValue = cartHtml.querySelector('[ref="hdt-cart"][data-count]')?.getAttribute('data-count');
  const count = countValue === null || countValue === undefined ? NaN : Number(countValue);
  if (!Number.isInteger(count) || count < 0) throw new Error('Unable to refresh the cart.');
  // Do not emit cart:update: the shipping-protection listener can mutate the
  // cart in response. Only reconcile counts; the error links to a fresh cart page.
  document.querySelectorAll('hdt-cart-count').forEach((element) => {
    element.textContent = String(count);
  });
}

export function getCartAddErrorMessage(error, fallback = CART_ADD_FRIENDLY_ERROR) {
  const message = error?.description || error?.message || error?.error;
  if (!message) return fallback;
  if (/Unexpected token|DOCTYPE|valid JSON|Failed to fetch|NetworkError|Load failed/i.test(message)) {
    return fallback;
  }
  return message;
}

export async function runFastMobileCartAdd({
  formData,
  sendAdd,
  onSuccess,
  refreshCart,
  onBackgroundError = console.error,
}) {
  formData.delete('sections');
  formData.delete('sections_url');

  const cartData = await sendAdd(formData);
  if (cartData?.status) {
    refreshCartInBackground(refreshCart, onBackgroundError);
    return { ok: false, data: cartData };
  }

  onSuccess?.(cartData);
  refreshCartInBackground(refreshCart, onBackgroundError);

  return { ok: true, data: cartData };
}

export function applyMobileProductCartPolicy(detail, context) {
  const isMobileProductAdd = Boolean(
    detail
    && detail.source === 'product-form-component'
    && isMobileProductContext(context)
  );

  if (!isMobileProductAdd) return false;

  detail.actionAfterATC = 'no_action';
  return true;
}

export async function handleFastMobileProductSubmit(event, {
  context,
  variantSearch = '',
  createFormData,
  sendAdd,
  onPending,
  onSuccess,
  onError,
  refreshCart,
  refreshUncertainCart,
  onBackgroundError,
}) {
  const form = event.target;
  const productForm = form?.closest?.('wrapp-hdt-pr-frm');
  const mainProductArea = productForm?.closest?.('#MainContent');
  if (!productForm || !mainProductArea || !isMobileProductContext(context)) return false;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (form.checkBreak) return true;
  if (!form.checkValidity()) {
    form.reportValidity();
    return true;
  }

  const button = productForm.querySelector('button[type="submit"][name="add"]');
  if (!button || button.hasAttribute('disabled')) return true;

  button.setAttribute('disabled', 'true');
  button.setAttribute('aria-busy', 'true');
  onPending?.(productForm, button);

  const releaseButton = () => {
    button.removeAttribute('disabled');
    button.removeAttribute('aria-busy');
  };

  const formData = createFormData(form);
  const variantId = applyVariantIdFallback(formData, variantSearch);
  if (!variantId) {
    releaseButton();
    onError?.({ message: VARIANT_REQUIRED_ERROR }, productForm, button);
    return true;
  }

  try {
    const result = await runFastMobileCartAdd({
      formData,
      sendAdd,
      onSuccess: (cartData) => {
        releaseButton();
        onSuccess?.(cartData, productForm, button);
      },
      refreshCart,
      onBackgroundError,
    });

    if (!result.ok) {
      releaseButton();
      onError?.(result.data, productForm, button);
    }
  } catch {
    releaseButton();
    // A lost or unreadable response does not mean Shopify rejected the add.
    // Never resend this non-idempotent operation; reconcile with a read-only refresh.
    refreshCartInBackground(refreshUncertainCart, onBackgroundError);
    onError?.({ message: CART_ADD_UNCERTAIN_ERROR, uncertain: true }, productForm, button);
  }

  return true;
}

function playFeedback(element, keyframes, duration) {
  if (!element?.animate) return Promise.resolve();

  feedbackAnimations.get(element)?.cancel();
  const animation = element.animate(keyframes, {
    duration,
    easing: EASE_OUT,
  });
  feedbackAnimations.set(element, animation);
  return animation.finished
    .catch(() => {})
    .finally(() => {
      if (feedbackAnimations.get(element) === animation) feedbackAnimations.delete(element);
    });
}

export function buildFlyToCartKeyframes({ deltaX, deltaY, reducedMotion }) {
  if (reducedMotion) return [{ opacity: 0.72 }, { opacity: 1 }];

  return [
    {
      transform: 'translate3d(0px, 0px, 0) scale(0.95)',
      opacity: 1,
    },
    {
      transform: `translate3d(${Math.round(deltaX * 0.58)}px, ${Math.round(deltaY * 0.48 - 18)}px, 0) scale(0.68)`,
      opacity: 0.92,
      offset: 0.56,
    },
    {
      transform: `translate3d(${Math.round(deltaX)}px, ${Math.round(deltaY)}px, 0) scale(0.35)`,
      opacity: 0.12,
    },
  ];
}

export function getCartItemImageUrl(cartData) {
  const item = cartData?.items?.[0] || cartData;
  const featuredImage = item?.featured_image;
  return featuredImage?.url || featuredImage?.src || item?.image || null;
}

export function getOptimisticCartCount({ countBefore, requestedQuantity }) {
  const baseCount = Number(countBefore);
  const addedQuantity = Number(requestedQuantity);
  if (!Number.isFinite(baseCount)) return null;
  return baseCount + (Number.isFinite(addedQuantity) && addedQuantity > 0 ? addedQuantity : 1);
}

export function findPreferredCartTarget(targets) {
  const visibleTargets = Array.from(targets).filter((target) => {
    const rect = target.getBoundingClientRect?.();
    return rect && rect.width > 0 && rect.height > 0;
  });

  return visibleTargets.find((target) => target.closest?.('.hdt-toolbar'))
    || visibleTargets[0]
    || null;
}

function getCartTarget() {
  const targets = document.querySelectorAll(
    '.hdt-toolbar .hdt-toolbart-cart a, [aria-controls="CartDrawer"]'
  );
  return findPreferredCartTarget(targets);
}

function updateCartCount(button) {
  const nextCount = getOptimisticCartCount({
    countBefore: button?.dataset.comelyCartCountBefore,
    requestedQuantity: button?.dataset.comelyAddedQuantity,
  });
  if (!Number.isFinite(nextCount)) return;
  document.querySelectorAll('hdt-cart-count').forEach((count) => {
    count.textContent = String(nextCount);
  });
}

function pulseCart(cartTarget, prefersReducedMotion) {
  return playFeedback(
    cartTarget,
    prefersReducedMotion
      ? [{ opacity: 0.72 }, { opacity: 1 }]
      : [
          { transform: 'scale(1)' },
          { transform: 'scale(1.12)', offset: 0.52 },
          { transform: 'scale(1)' },
        ],
    160
  );
}

async function flyProductToCart(button, cartData, prefersReducedMotion) {
  const cartTarget = getCartTarget();
  if (!cartTarget) return;

  if (prefersReducedMotion) {
    updateCartCount(button);
    await pulseCart(cartTarget, true);
    return;
  }

  const imageUrl = getCartItemImageUrl(cartData);
  const sourceRect = button.getBoundingClientRect?.();
  const targetRect = cartTarget.getBoundingClientRect?.();
  if (!imageUrl || !sourceRect || !targetRect || !document.body) {
    updateCartCount(button);
    await pulseCart(cartTarget, false);
    return;
  }

  const size = 44;
  const sourceCenterX = sourceRect.left + sourceRect.width / 2;
  const sourceCenterY = sourceRect.top + sourceRect.height / 2;
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;
  const flyer = document.createElement('img');
  flyer.src = imageUrl;
  flyer.alt = '';
  flyer.setAttribute('aria-hidden', 'true');
  Object.assign(flyer.style, {
    position: 'fixed',
    left: `${sourceCenterX - size / 2}px`,
    top: `${sourceCenterY - size / 2}px`,
    width: `${size}px`,
    height: `${size}px`,
    objectFit: 'cover',
    borderRadius: '11px',
    boxShadow: '0 8px 20px rgba(20, 14, 15, 0.22)',
    pointerEvents: 'none',
    zIndex: '2147483000',
    willChange: 'transform, opacity',
  });
  document.body.append(flyer);

  const keyframes = buildFlyToCartKeyframes({
    deltaX: targetCenterX - sourceCenterX,
    deltaY: targetCenterY - sourceCenterY,
    reducedMotion: false,
  });
  const animation = flyer.animate?.(keyframes, {
    duration: FLY_DURATION,
    easing: EASE_IN_OUT,
    fill: 'forwards',
  });

  if (animation?.finished) await animation.finished.catch(() => {});
  flyer.remove();
  updateCartCount(button);
  await pulseCart(cartTarget, false);
}

function getButtonParts(eventTarget) {
  const button = eventTarget?.querySelector?.('button[type="submit"][name="add"]');
  const label = button?.querySelector('.hdt-btn-atc_text');
  return { button, label };
}

function showPendingFeedback(eventTarget) {
  const { button, label } = getButtonParts(eventTarget);
  if (!button || !label) return;

  const originalLabel = label.dataset.comelyOriginalLabel || label.textContent.trim();
  label.dataset.comelyOriginalLabel = originalLabel;
  label.textContent = PENDING_LABEL;

  const currentCount = document.querySelector('.hdt-toolbar .hdt-toolbart-cart hdt-cart-count');
  const countBefore = Number(currentCount?.textContent?.trim());
  if (Number.isFinite(countBefore)) button.dataset.comelyCartCountBefore = String(countBefore);
  const quantity = Number(eventTarget.querySelector?.('[name="quantity"]')?.value);
  button.dataset.comelyAddedQuantity = String(Number.isFinite(quantity) && quantity > 0 ? quantity : 1);

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  void playFeedback(
    button,
    prefersReducedMotion
      ? [{ opacity: 0.82 }, { opacity: 1 }]
      : [
          { transform: 'scale(0.97)', opacity: 0.86 },
          { transform: 'scale(1)', opacity: 1 },
        ],
    120
  );
}

function restoreButtonFeedback(eventTarget) {
  const { button, label } = getButtonParts(eventTarget);
  if (!button || !label) return;
  if (label.dataset.comelyOriginalLabel) label.textContent = label.dataset.comelyOriginalLabel;
  delete label.dataset.comelyOriginalLabel;
  delete button.dataset.comelyCartCountBefore;
  delete button.dataset.comelyAddedQuantity;
}

function showSuccessFeedback(eventTarget, cartData) {
  const { button, label } = getButtonParts(eventTarget);
  if (!button || !label) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const originalLabel = label.dataset.comelyOriginalLabel || label.textContent.trim();
  label.dataset.comelyOriginalLabel = originalLabel;
  label.textContent = SUCCESS_LABEL;

  void playFeedback(
    button,
    prefersReducedMotion
      ? [{ opacity: 0.72 }, { opacity: 1 }]
      : [
          { transform: 'scale(0.97)', opacity: 0.82 },
          { transform: 'scale(1)', opacity: 1 },
        ],
    160
  );

  void flyProductToCart(button, cartData, prefersReducedMotion);

  clearTimeout(feedbackTimers.get(button));
  feedbackTimers.set(button, setTimeout(() => {
    if (label.textContent.trim() === SUCCESS_LABEL) label.textContent = originalLabel;
    delete label.dataset.comelyOriginalLabel;
    delete button.dataset.comelyCartCountBefore;
    delete button.dataset.comelyAddedQuantity;
    feedbackTimers.delete(button);
  }, SUCCESS_LABEL_DURATION));
}

if (typeof document !== 'undefined') {
  document.addEventListener('submit', (event) => {
    const context = {
      viewportWidth: window.innerWidth,
      pageType: document.body.classList.contains('hdt-page-type-product') ? 'product' : '',
    };

    void handleFastMobileProductSubmit(event, {
      context,
      variantSearch: window.location.search,
      createFormData: (form) => new FormData(form),
      sendAdd: async (payload) => {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: payload,
        });
        return parseCartAddResponse(response);
      },
      onPending: (productForm, button) => {
        button.setAttribute('aria-live', 'polite');
        showPendingFeedback(productForm);
      },
      onSuccess: (cartData, productForm, button) => {
        button.setAttribute('aria-live', 'polite');
        productForm.querySelector(
          '[ref="addToCartTextError"], .hdt-form__message-wrapper.hdt-form__message--error'
        )?.setAttribute('hidden', '');
        showSuccessFeedback(productForm, cartData);
      },
      onError: (error, productForm) => {
        restoreButtonFeedback(productForm);
        const message = getCartAddErrorMessage(
          error,
          window.themeHDN?.extras?.cart?.error_message || CART_ADD_FRIENDLY_ERROR
        );
        const errorMessage = productForm.querySelector(
          '[ref="addToCartTextError"], .hdt-form__message-wrapper.hdt-form__message--error'
        );
        if (errorMessage && message) {
          errorMessage.removeAttribute('hidden');
          const text = errorMessage.querySelector('.hdt-error-message');
          if (text) {
            text.textContent = message;
            if (error.uncertain) {
              const cartLink = document.createElement('a');
              cartLink.href = `${Shopify.routes.root}cart`;
              cartLink.textContent = 'View cart';
              text.append(' ', cartLink);
            }
          }
        }
        window.dispatchEvent(new CustomEvent('cart:error', {
          detail: {
            sourceId: productForm.id,
            data: { message },
          },
        }));
      },
      refreshCart: () => {
        document.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
      },
      refreshUncertainCart: refreshCartReadOnly,
      onBackgroundError: (error) => console.error(error),
    });
  }, { capture: true });

  document.addEventListener('cart:update', (event) => {
    const handled = applyMobileProductCartPolicy(event.detail, {
      viewportWidth: window.innerWidth,
      pageType: document.body.classList.contains('hdt-page-type-product') ? 'product' : '',
    });

    if (handled) showSuccessFeedback(event.target, event.detail?.data);
  }, { capture: true });
}
